import { api, type ApiClient } from '@/helpers/apiClient'
import {
  opsTenantDetailDtoSchema,
  opsTenantListDtoSchema,
  teamDtoSchema,
  teamInvitationDtoSchema,
  tenantActivityListDtoSchema,
  type OpsTenantDetailDto,
  type OpsTenantListDto,
  type TeamDto,
  type TeamInvitationDto,
  type TenantActivityListDto,
} from '@lib/dto/team.dto'
import { apiKeyDtoSchema, apiKeyListDtoSchema, type ApiKeyDto, type ApiKeyListDto } from '@lib/dto/tenant-settings.dto'
import type { ChangeMemberRoleInput, InviteMemberInput } from '@lib/input/team.input'
import type { CloseTenantInput, CreateTenantInput, SuspendTenantInput, TenantProfileInput } from '@lib/input/tenant.input'
import type { Uuid } from '@lib/validators/params'
import type { IOpsTenantService, ITeamService } from '@/types/TeamService.types'

function teamPath(tenantId: Uuid): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}`
}

/**
 * A tenant's team and activity through the dashboard API. Bound to a session's
 * client: the tenant user's own, or staff's when viewing as the tenant.
 */
export class TeamService implements ITeamService {
  constructor(private readonly client: ApiClient) {}

  get(tenantId: Uuid): Promise<TeamDto> {
    return this.client.get(`${teamPath(tenantId)}/team`, teamDtoSchema)
  }

  invite(tenantId: Uuid, input: InviteMemberInput): Promise<TeamInvitationDto> {
    return this.client.post(`${teamPath(tenantId)}/invitations`, input, teamInvitationDtoSchema)
  }

  resend(tenantId: Uuid, invitationId: Uuid): Promise<TeamInvitationDto> {
    return this.client.post(
      `${teamPath(tenantId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
      {},
      teamInvitationDtoSchema,
    )
  }

  async revoke(tenantId: Uuid, invitationId: Uuid): Promise<void> {
    await this.client.del(`${teamPath(tenantId)}/invitations/${encodeURIComponent(invitationId)}`)
  }

  changeRole(tenantId: Uuid, userId: Uuid, input: ChangeMemberRoleInput): Promise<TeamDto> {
    return this.client.patch(
      `${teamPath(tenantId)}/members/${encodeURIComponent(userId)}`,
      input,
      teamDtoSchema,
    )
  }

  async remove(tenantId: Uuid, userId: Uuid): Promise<void> {
    await this.client.del(`${teamPath(tenantId)}/members/${encodeURIComponent(userId)}`)
  }

  activity(tenantId: Uuid): Promise<TenantActivityListDto> {
    return this.client.get(`${teamPath(tenantId)}/activity`, tenantActivityListDtoSchema)
  }
}

function opsTenantPath(tenantId: Uuid): string {
  return `/ops/tenants/${encodeURIComponent(tenantId)}`
}

class OpsTenantService implements IOpsTenantService {
  list(): Promise<OpsTenantListDto> {
    return api.get('/ops/tenants', opsTenantListDtoSchema)
  }

  create(input: CreateTenantInput): Promise<OpsTenantDetailDto> {
    return api.post('/ops/tenants', input, opsTenantDetailDtoSchema)
  }

  update(tenantId: Uuid, input: TenantProfileInput): Promise<OpsTenantDetailDto> {
    return api.patch(opsTenantPath(tenantId), input, opsTenantDetailDtoSchema)
  }

  suspend(tenantId: Uuid, input: SuspendTenantInput): Promise<OpsTenantDetailDto> {
    return api.post(`${opsTenantPath(tenantId)}/suspend`, input, opsTenantDetailDtoSchema)
  }

  reactivate(tenantId: Uuid): Promise<OpsTenantDetailDto> {
    return api.post(`${opsTenantPath(tenantId)}/reactivate`, {}, opsTenantDetailDtoSchema)
  }

  close(tenantId: Uuid, input: CloseTenantInput): Promise<OpsTenantDetailDto> {
    return api.post(`${opsTenantPath(tenantId)}/close`, input, opsTenantDetailDtoSchema)
  }

  reopen(tenantId: Uuid): Promise<OpsTenantDetailDto> {
    return api.post(`${opsTenantPath(tenantId)}/reopen`, {}, opsTenantDetailDtoSchema)
  }

  activity(tenantId: Uuid): Promise<TenantActivityListDto> {
    return api.get(`${opsTenantPath(tenantId)}/activity`, tenantActivityListDtoSchema)
  }

  listKeys(tenantId: Uuid): Promise<ApiKeyListDto> {
    return api.get(`${opsTenantPath(tenantId)}/api-keys`, apiKeyListDtoSchema)
  }

  revokeKey(tenantId: Uuid, keyId: Uuid): Promise<ApiKeyDto> {
    return api.post(`${opsTenantPath(tenantId)}/api-keys/${encodeURIComponent(keyId)}/revoke`, {}, apiKeyDtoSchema)
  }

  get(tenantId: Uuid): Promise<OpsTenantDetailDto> {
    return api.get(`/ops/tenants/${encodeURIComponent(tenantId)}`, opsTenantDetailDtoSchema)
  }

  invite(tenantId: Uuid, input: InviteMemberInput): Promise<TeamInvitationDto> {
    return api.post(
      `/ops/tenants/${encodeURIComponent(tenantId)}/invitations`,
      input,
      teamInvitationDtoSchema,
    )
  }

  resend(tenantId: Uuid, invitationId: Uuid): Promise<TeamInvitationDto> {
    return api.post(
      `/ops/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}/resend`,
      {},
      teamInvitationDtoSchema,
    )
  }

  async revoke(tenantId: Uuid, invitationId: Uuid): Promise<void> {
    await api.del(
      `/ops/tenants/${encodeURIComponent(tenantId)}/invitations/${encodeURIComponent(invitationId)}`,
    )
  }
}

export const opsTenantService = new OpsTenantService()
